import { useEffect, useRef, useState, type CSSProperties } from "react";

import { useReducedMotion } from "@/hooks/useReducedMotion";
import type { ServiceChapter } from "@/config/serviceArchiveData";
import {
  homepageImageUrl,
  type HomepageServicesContent,
} from "@/data/homepageContent";

import styles from "./ServicesSection.module.scss";

const DESKTOP_MEDIA = "(min-width: 768px)";
// Must match the section width set in ServicesSection.module.scss / HorizontalTrack
// (data-stage-width="services") and experience.scss (.panel-services): the chapters
// + filmstrip portion, followed by the image-reveal portion appended after it.
const SERVICES_WIDTH_VW = 450;
const REVEAL_WIDTH_VW = 70;
const FULLSCREEN_WIDTH_VW = 50;
const TOTAL_SERVICES_WIDTH_VW = SERVICES_WIDTH_VW + REVEAL_WIDTH_VW + FULLSCREEN_WIDTH_VW;
const SERVICES_PROGRESS_EASING = 0.16;
const SERVICES_PROGRESS_SNAP = 0.0006;
const SERVICES_HANDOFF_SNAP = 0.998;

type ChapterMetrics = {
  left: number;
  width: number;
  opacity: number;
  openness: number;
  imageScale: number;
  imageShift: number;
  zIndex: number;
  mode: "collapsed" | "active" | "incoming" | "future";
};

type ServicesSectionProps = {
  content: HomepageServicesContent;
  forceDesktopStage?: boolean;
};

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function lerp(from: number, to: number, progress: number) {
  return from + (to - from) * progress;
}

function getContentOpacity(metrics: ChapterMetrics, reducedMotion: boolean) {
  if (reducedMotion) {
    return metrics.mode === "future" ? 0 : 1;
  }

  if (metrics.mode === "incoming") {
    return clamp((metrics.openness - 0.82) / 0.16, 0, 1);
  }

  if (metrics.mode === "active") {
    return clamp((metrics.openness - 0.82) / 0.14, 0, 1);
  }

  return 0;
}

// Reading, gap, settle: burns most of a chapter transition's scroll distance on the
// parts where content is visible, and rushes through the "nothing legible yet" middle.
const TRANSITION_PACING: Array<[scrollT: number, local: number]> = [
  [0, 0],
  [0.25, 0.18],
  [0.4, 0.82],
  [1, 1],
];

function remapTransitionLocal(t: number) {
  const clamped = clamp(t, 0, 1);

  for (let i = 1; i < TRANSITION_PACING.length; i += 1) {
    const [prevT, prevLocal] = TRANSITION_PACING[i - 1];
    const [nextT, nextLocal] = TRANSITION_PACING[i];

    if (clamped <= nextT) {
      const span = Math.max(nextT - prevT, 0.0001);
      return lerp(prevLocal, nextLocal, (clamped - prevT) / span);
    }
  }

  return 1;
}

// Once the last service has settled at finalPartnerWidth, further scroll (the
// filmstrip phase) cycles its image through the other three services before
// narrowing it into a compact services CTA slide. cycleEnd is how much of that
// phase is spent cycling images; the remainder narrows the panel into the CTA.
const FILMSTRIP_CYCLE_END = 0.82;

function getServicesMediaWidth(
  filmstripProgress: number,
  finalPartnerWidth: number,
  ctaWidth: number,
) {
  const ctaReveal = clamp(
    (filmstripProgress - FILMSTRIP_CYCLE_END) / Math.max(1 - FILMSTRIP_CYCLE_END, 0.0001),
    0,
    1,
  );
  return lerp(finalPartnerWidth, ctaWidth, ctaReveal);
}

function getDesktopChapterMetrics(
  index: number,
  stage: number,
  total: number,
  viewportWidth: number,
  reducedMotion: boolean,
  filmstripProgress = 0,
): ChapterMetrics {
  const lastIndex = total - 1;
  const collapsedWidth = clamp(viewportWidth * 0.082, 84, 118);
  const previewWidth = clamp(viewportWidth * 0.132, 170, 238);
  const transitionCount = Math.max(total - 1, 1);
  // The final transition doesn't hand off to another service: it hands off to the
  // "from screen to space" panel, so the outgoing chapter settles at just over half
  // width instead of collapsing down to the thin filmstrip the other chapters use.
  const finalPartnerWidth = clamp(viewportWidth * 0.56, 320, viewportWidth * 0.62);
  const servicesCtaWidth = clamp(viewportWidth * 0.34, 300, 460);
  const mediaWidth = getServicesMediaWidth(filmstripProgress, finalPartnerWidth, servicesCtaWidth);
  const collapsedLeft = (slotIndex: number) => slotIndex * collapsedWidth;

  if (reducedMotion) {
    const left = index === 0 ? 0 : viewportWidth - previewWidth;
    const width = index === 0 ? viewportWidth : previewWidth;

    return {
      left,
      width,
      opacity: index === 0 ? 1 : 0.35,
      openness: index === 0 ? 1 : 0,
      imageScale: 1.02,
      imageShift: 0,
      zIndex: total - index,
      mode: index === 0 ? "active" : "future",
    };
  }

  if (stage >= transitionCount) {
    const hostIndex = Math.max(lastIndex - 1, 0);
    const hostLeft = collapsedLeft(hostIndex);

    if (index < hostIndex) {
      return {
        left: collapsedLeft(index),
        width: collapsedWidth,
        opacity: 1,
        openness: 0,
        imageScale: 1.02,
        imageShift: -1.8,
        zIndex: total + index,
        mode: "collapsed",
      };
    }

    if (index === lastIndex) {
      const left = total > 1 ? hostLeft + mediaWidth : 0;
      const width = viewportWidth - left;

      return {
        left,
        width,
        opacity: 1,
        openness: 1,
        imageScale: 1.02,
        imageShift: -2.1,
        zIndex: total + 6,
        mode: "active",
      };
    }

    if (index === hostIndex) {
      return {
        left: hostLeft,
        width: mediaWidth,
        opacity: 1,
        openness: clamp(
          (mediaWidth - collapsedWidth) / Math.max(viewportWidth - collapsedWidth, 1),
          0,
          1,
        ),
        imageScale: 1.02,
        imageShift: -2.8,
        zIndex: total + index,
        mode: "collapsed",
      };
    }

    return {
      left: viewportWidth,
      width: collapsedWidth,
      opacity: 0,
      openness: 0,
      imageScale: 1.02,
      imageShift: -2.8,
      zIndex: 0,
      mode: "future",
    };
  }

  const currentIndex = clamp(Math.floor(stage), 0, total - 2);
  const local = clamp(stage - currentIndex, 0, 1);
  const isFinalTransition = currentIndex === lastIndex - 1;
  const currentCollapseTarget = isFinalTransition ? finalPartnerWidth : collapsedWidth;
  const settledStripCount = currentIndex;
  const currentLeft = collapsedLeft(settledStripCount);
  const currentOpenWidth = viewportWidth - currentLeft;
  const currentWidth = lerp(currentOpenWidth, currentCollapseTarget, local);
  const nextIndex = currentIndex + 1;
  const nextLeft = lerp(viewportWidth - previewWidth, currentLeft + currentCollapseTarget, local);
  const nextWidth = viewportWidth - nextLeft;

  if (index < currentIndex) {
    return {
      left: collapsedLeft(index),
      width: collapsedWidth,
      opacity: 1,
      openness: 0,
      imageScale: 1.02,
      imageShift: -1.8,
      zIndex: total + index,
      mode: "collapsed",
    };
  }

  if (index === currentIndex) {
    const openness = clamp(
      (currentWidth - currentCollapseTarget) / Math.max(currentOpenWidth - currentCollapseTarget, 1),
      0,
      1,
    );

    return {
      left: currentLeft,
      width: currentWidth,
      opacity: 1,
      openness,
      imageScale: 1.015 + openness * 0.018,
      imageShift: lerp(0, -2.4, local),
      // Always above the incoming chapter so its growing image never paints over
      // this chapter's still-visible title during the overlap band of the transition.
      zIndex: total * 3,
      mode: openness > 0.14 ? "active" : "collapsed",
    };
  }

  if (index === nextIndex) {
    const openness = clamp(
      (nextWidth - previewWidth) / Math.max(viewportWidth - currentCollapseTarget - previewWidth, 1),
      0,
      1,
    );

    return {
      left: nextLeft,
      width: nextWidth,
      opacity: 1,
      openness,
      imageScale: lerp(1.05, 1.02, local),
      imageShift: lerp(1.4, -0.6, local),
      zIndex: total + 2,
      mode: local < 0.98 ? "incoming" : "active",
    };
  }

  if (index > nextIndex) {
    return {
      left: viewportWidth,
      width: collapsedWidth,
      opacity: 0,
      openness: 0,
      imageScale: 1.06,
      imageShift: 2.2,
      zIndex: 0,
      mode: "future",
    };
  }

  if (index === lastIndex && stage >= lastIndex) {
    const left = collapsedWidth;
    const width = viewportWidth - left;

    return {
      left,
      width,
      opacity: 1,
      openness: 1,
      imageScale: 1.04,
      imageShift: -3.6,
      zIndex: total + 6,
      mode: "active",
    };
  }

  return {
    left: viewportWidth,
    width: previewWidth,
    opacity: 0,
    openness: 0,
    imageScale: 1.08,
    imageShift: 2.8,
    zIndex: 0,
    mode: "future",
  };
}

export function ServicesSection({ content, forceDesktopStage = false }: ServicesSectionProps) {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const reducedMotion = useReducedMotion();
  const serviceChapters: ServiceChapter[] = content.chapters.map((chapter) => ({
    id: chapter.key,
    eyebrow: chapter.eyebrow,
    label: chapter.label,
    title: chapter.title,
    body: chapter.body,
    note: chapter.note,
    image: homepageImageUrl(chapter.image),
    imagePosition: chapter.image.position,
    surface: chapter.surface_color,
    accent: chapter.accent_color,
    href: chapter.href,
  }));
  const screenToSpace = {
    id: "screen-to-space",
    eyebrow: content.transition_eyebrow,
    title: content.transition_title,
    body: content.transition_body,
  };
  const imageRevealSrc = homepageImageUrl(content.reveal_image);
  const [progress, setProgress] = useState(0);
  const [revealProgress, setRevealProgress] = useState(0);
  const [fullscreenProgress, setFullscreenProgress] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window === "undefined" ? 1440 : window.innerWidth,
  );
  const [viewportHeight, setViewportHeight] = useState(
    typeof window === "undefined" ? 900 : window.innerHeight,
  );
  const [isDesktop, setIsDesktop] = useState(
    typeof window === "undefined" ? true : window.matchMedia(DESKTOP_MEDIA).matches,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(DESKTOP_MEDIA);
    const update = () => setIsDesktop(mediaQuery.matches);

    update();
    mediaQuery.addEventListener("change", update);
    return () => mediaQuery.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const updateViewportSize = () => {
      setViewportWidth(window.innerWidth);
      setViewportHeight(window.innerHeight);
    };

    updateViewportSize();
    window.addEventListener("resize", updateViewportSize);
    return () => window.removeEventListener("resize", updateViewportSize);
  }, []);

  useEffect(() => {
    const section = sectionRef.current;
    const viewport = document.querySelector<HTMLElement>("[data-stage-viewport]");
    const stageSection = section?.closest<HTMLElement>("[data-stage-section]");

    if (!section || !stageSection) {
      return;
    }

    const desktopQuery = window.matchMedia(DESKTOP_MEDIA);
    let frameId: number | null = null;
    let visualProgress = 0;
    let visualRevealProgress = 0;
    let visualFullscreenProgress = 0;

    // The section's total scroll width is split into three portions: chapters +
    // filmstrip (SERVICES_WIDTH_VW), then the text -> framed-image slide-in
    // (REVEAL_WIDTH_VW), then the frame expanding to true full-screen while the
    // left-hand stack clears away (FULLSCREEN_WIDTH_VW) — see .panel-services /
    // [data-stage-width] for the matching total (TOTAL_SERVICES_WIDTH_VW).
    const computeDesktopProgress = () => {
      if (!viewport) {
        return { progress: 0, revealProgress: 0, fullscreenProgress: 0 };
      }

      const start = stageSection.offsetLeft;
      const totalScrollable = Math.max(stageSection.offsetWidth - viewport.clientWidth, 1);
      const scrolled = viewport.scrollLeft - start;
      const splitPoint1 = totalScrollable * (SERVICES_WIDTH_VW / TOTAL_SERVICES_WIDTH_VW);
      const splitPoint2 =
        totalScrollable * ((SERVICES_WIDTH_VW + REVEAL_WIDTH_VW) / TOTAL_SERVICES_WIDTH_VW);

      return {
        progress: clamp(scrolled / Math.max(splitPoint1, 1), 0, 1),
        revealProgress: clamp(
          (scrolled - splitPoint1) / Math.max(splitPoint2 - splitPoint1, 1),
          0,
          1,
        ),
        fullscreenProgress: clamp(
          (scrolled - splitPoint2) / Math.max(totalScrollable - splitPoint2, 1),
          0,
          1,
        ),
      };
    };

    const computeMobileProgress = () => {
      const rect = section.getBoundingClientRect();
      const distance = window.innerHeight - rect.top;
      const totalDistance = Math.max(rect.height - window.innerHeight * 0.25, 1);
      return {
        progress: clamp(distance / totalDistance, 0, 1),
        revealProgress: 0,
        fullscreenProgress: 0,
      };
    };

    const render = () => {
      const desktop = desktopQuery.matches || forceDesktopStage;
      const nextTarget = desktop ? computeDesktopProgress() : computeMobileProgress();
      const isAtDesktopHandoff =
        desktop && nextTarget.fullscreenProgress >= SERVICES_HANDOFF_SNAP;

      if (isAtDesktopHandoff) {
        visualProgress = nextTarget.progress;
        visualRevealProgress = nextTarget.revealProgress;
        visualFullscreenProgress = nextTarget.fullscreenProgress;
      } else {
        visualProgress += (nextTarget.progress - visualProgress) * SERVICES_PROGRESS_EASING;
        visualRevealProgress +=
          (nextTarget.revealProgress - visualRevealProgress) * SERVICES_PROGRESS_EASING;
        visualFullscreenProgress +=
          (nextTarget.fullscreenProgress - visualFullscreenProgress) * SERVICES_PROGRESS_EASING;
      }

      if (Math.abs(nextTarget.progress - visualProgress) < SERVICES_PROGRESS_SNAP) {
        visualProgress = nextTarget.progress;
      }

      if (Math.abs(nextTarget.revealProgress - visualRevealProgress) < SERVICES_PROGRESS_SNAP) {
        visualRevealProgress = nextTarget.revealProgress;
      }

      if (Math.abs(nextTarget.fullscreenProgress - visualFullscreenProgress) < SERVICES_PROGRESS_SNAP) {
        visualFullscreenProgress = nextTarget.fullscreenProgress;
      }

      setProgress((current) =>
        Math.abs(current - visualProgress) > 0.0005 ? visualProgress : current,
      );
      setRevealProgress((current) =>
        Math.abs(current - visualRevealProgress) > 0.0005 ? visualRevealProgress : current,
      );
      setFullscreenProgress((current) =>
        Math.abs(current - visualFullscreenProgress) > 0.0005 ? visualFullscreenProgress : current,
      );

      frameId = window.requestAnimationFrame(render);
    };

    const requestRender = () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }

      frameId = window.requestAnimationFrame(render);
    };

    requestRender();

    viewport?.addEventListener("scroll", requestRender, { passive: true });
    window.addEventListener("scroll", requestRender, { passive: true });
    window.addEventListener("resize", requestRender);
    desktopQuery.addEventListener("change", requestRender);

    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }

      viewport?.removeEventListener("scroll", requestRender);
      window.removeEventListener("scroll", requestRender);
      window.removeEventListener("resize", requestRender);
      desktopQuery.removeEventListener("change", requestRender);
    };
  }, [forceDesktopStage]);

  if (!isDesktop && !forceDesktopStage) {
    return (
      <div ref={sectionRef} className={styles.mobileServices}>
        {serviceChapters.map((chapter) => (
          <article key={chapter.id} className={styles.mobileCard}>
            <div
              className={styles.mobileImage}
              style={{
                backgroundImage: `url('${chapter.image}')`,
                backgroundPosition: chapter.imagePosition,
              }}
            />
            <div className={styles.mobileCopy}>
              <span className={styles.mobileEyebrow}>{chapter.eyebrow}</span>
              <span className={styles.mobileLabel}>{chapter.label}</span>
              <h2>{chapter.title}</h2>
              <p className={styles.mobileNote}>{chapter.note}</p>
              <p>{chapter.body}</p>
            </div>
          </article>
        ))}

        <article className={styles.mobileCard}>
          <div className={styles.mobileCopy}>
            <span className={styles.mobileEyebrow}>{screenToSpace.eyebrow}</span>
            <h2>{screenToSpace.title}</h2>
            <p>{screenToSpace.body}</p>
          </div>
          <div
            className={styles.mobileImage}
            style={{
              backgroundImage: `url('${imageRevealSrc}')`,
              backgroundPosition: content.reveal_image.position,
            }}
          />
        </article>
      </div>
    );
  }

  const panelCount = serviceChapters.length + 1;
  const transitionCount = Math.max(panelCount - 1, 1);
  // Chapter transitions only use the first CHAPTER_PHASE_FRACTION of this section's
  // scroll distance; the remainder drives the post-settle image filmstrip below.
  const CHAPTER_PHASE_FRACTION = 0.74;
  const chapterProgress = clamp(progress / CHAPTER_PHASE_FRACTION, 0, 1);
  const filmstripProgress = clamp(
    (progress - CHAPTER_PHASE_FRACTION) / Math.max(1 - CHAPTER_PHASE_FRACTION, 0.0001),
    0,
    1,
  );
  const rawStage = chapterProgress * transitionCount;
  const stageIndex = clamp(Math.floor(rawStage), 0, transitionCount - 1);
  const stageLocal = rawStage - stageIndex;
  const holdRatio = 0.06;
  const moveLocal =
    stageLocal <= holdRatio
      ? 0
      : remapTransitionLocal((stageLocal - holdRatio) / Math.max(1 - holdRatio, 0.0001));
  const stage = rawStage >= transitionCount ? transitionCount : stageIndex + clamp(moveLocal, 0, 1);
  const chapterMetrics = Array.from({ length: panelCount }, (_, index) =>
    getDesktopChapterMetrics(index, stage, panelCount, viewportWidth, reducedMotion, filmstripProgress),
  );

  // Filmstrip: once the last service has settled, its image cycles through the
  // other three services (bottom to top) before the panel narrows into a CTA slide.
  const filmstripCycle = clamp(filmstripProgress / FILMSTRIP_CYCLE_END, 0, 1);
  // There is a real stretch — from when the last chapter's own text has finished
  // fading out, to when the CTA is ready to appear — where there is nothing to show
  // in the text strip. Rather than try to keep something (however faint) alive in
  // that strip, let the photo itself take over the full frame for that stretch: the
  // text row collapses to 0 as the old text finishes fading, stays collapsed while
  // the image (and, once settled, the filmstrip stack) fills the whole card, then
  // re-opens as the CTA fades in. Nothing is ever empty because there is no empty
  // row on screen during that stretch — just the photo, full height.
  const lastChapterIndex = serviceChapters.length - 1;
  const crossfadeStart = lastChapterIndex;
  const crossfadeEnd = lastChapterIndex + 1 + FILMSTRIP_CYCLE_END;
  const combinedProgress = stage + filmstripProgress;
  const crossfadeT = clamp(
    (combinedProgress - crossfadeStart) / Math.max(crossfadeEnd - crossfadeStart, 0.0001),
    0,
    1,
  );
  const oldTextFadeOutEnd = 0.3;
  const rowReopenStart = 0.78;
  const rowReopenEnd = 0.92;
  const finalOldOpacity = 1 - clamp(crossfadeT / oldTextFadeOutEnd, 0, 1);
  const finalCtaOpacity = clamp(
    (crossfadeT - rowReopenStart) / (rowReopenEnd - rowReopenStart),
    0,
    1,
  );
  // Text row open fraction: 1 while old text is visible, 0 through the image-only
  // buffer, back to 1 in step with the CTA fading in — same shape as finalOldOpacity
  // on the way down and finalCtaOpacity on the way up, so the row never collapses or
  // opens out of sync with whatever content is (or isn't) supposed to be inside it.
  const rowOpenFraction = crossfadeT <= oldTextFadeOutEnd
    ? 1 - clamp(crossfadeT / oldTextFadeOutEnd, 0, 1)
    : finalCtaOpacity;
  const stripDismissDriver = Math.max(
    clamp((filmstripCycle - 0.34) / 0.34, 0, 1),
    clamp((revealProgress - 0.24) / 0.34, 0, 1),
    clamp(fullscreenProgress / 0.86, 0, 1),
  );
  const stripDismiss = reducedMotion
    ? clamp(stripDismissDriver, 0, 1)
    : clamp(stripDismissDriver, 0, 1);
  const stripDismissEased = reducedMotion
    ? stripDismiss
    : 1 - Math.pow(1 - stripDismiss, 1.95);
  const collapsedStripWidth = clamp(viewportWidth * 0.082, 84, 118);
  const dismissibleStripIndices = serviceChapters.reduce<number[]>((indices, _, chapterIndex) => {
    if (chapterMetrics[chapterIndex]?.mode === "collapsed" && chapterIndex < lastChapterIndex) {
      indices.push(chapterIndex);
    }

    return indices;
  }, []);
  const getReclaimedWidthBefore = (index: number) =>
    dismissibleStripIndices.filter((stripIndex) => stripIndex < index).length *
    collapsedStripWidth *
    stripDismissEased;
  const totalReclaimedStripWidth = dismissibleStripIndices.length * collapsedStripWidth * stripDismissEased;
  // The last service's image is already fully visible when this phase starts, so
  // it starts pre-filled; the other three each get their own equal window to grow
  // into their permanent slot, stacking underneath — nothing already shown is ever
  // removed or slid away, each new image just claims its own fixed share of height.
  const stackFrames = [...serviceChapters].reverse();
  const stackSlotProgress = stackFrames.map((_, slotIndex) => {
    if (slotIndex === 0) {
      return 1;
    }

    const growingSlots = stackFrames.length - 1;
    const slotStart = (slotIndex - 1) / growingSlots;
    const slotEnd = slotIndex / growingSlots;
    return clamp((filmstripCycle - slotStart) / Math.max(slotEnd - slotStart, 0.0001), 0, 1);
  });

  return (
    <div
      ref={sectionRef}
      className={`${styles.services} ${forceDesktopStage ? styles.forceDesktopStage : ""}`.trim()}
    >
      <div id="servicesPinShell" className={styles.pinShell}>
        <div className={styles.backdrop} aria-hidden="true" />
        <div className={styles.backdropGlow} aria-hidden="true" />

        {chapterMetrics[serviceChapters.length] && (() => {
          const metrics = chapterMetrics[serviceChapters.length];
          const panelReclaimedWidth = totalReclaimedStripWidth;
          const reclaimedPanelLeft = Math.max(metrics.left - panelReclaimedWidth, 0);
          const reclaimedPanelWidth = Math.min(
            metrics.width + panelReclaimedWidth,
            viewportWidth - reclaimedPanelLeft,
          );
          const contentOpacity = getContentOpacity(metrics, reducedMotion);
          // Elaborate, staggered entrance (eyebrow, then title, then body each on
          // their own reveal window) that fully settles into a static rest state
          // once contentOpacity reaches 1 — no motion continues after that point.
          const eyebrowT = reducedMotion ? contentOpacity : clamp(contentOpacity / 0.42, 0, 1);
          const titleT = reducedMotion ? contentOpacity : clamp((contentOpacity - 0.16) / 0.56, 0, 1);
          const bodyT = reducedMotion ? contentOpacity : clamp((contentOpacity - 0.4) / 0.6, 0, 1);

          // Once the chapters + filmstrip are fully settled (the left-hand stack is
          // frozen at this point — its width only depends on filmstripProgress, which
          // is already at 1), the extra scroll room drives this same panel from text
          // into a framed photo: text clears out first, then the (already full-size,
          // already fully framed) photo slides in from off-screen right and settles
          // into place — nothing scales up from small, it is dragged into position —
          // the left stack never moves, no section-level scroll-away, just this
          // right-hand panel changing what it shows.
          const revealTextT = reducedMotion ? 1 : 1 - clamp(revealProgress / 0.28, 0, 1);
          const imageT = reducedMotion ? 1 : clamp((revealProgress - 0.32) / 0.68, 0, 1);
          const imageEased = 1 - Math.pow(1 - imageT, 1.6);
          const imageTranslateX = reducedMotion ? 0 : lerp(42, 0, imageEased);
          const imageShadowAlpha = reducedMotion ? 0.24 : lerp(0.34, 0.24, imageEased);
          const captionT = reducedMotion ? 1 : clamp((revealProgress - 0.88) / 0.12, 0, 1);

          // Final beat: once the framed photo has settled, further scroll expands
          // this panel from "the right-hand share of the screen" to the whole
          // viewport — the left-hand stack (rendered below) shrinks away in step —
          // so only the framed photo is left on screen, full screen.
          const fullscreenEased = reducedMotion
            ? fullscreenProgress
            : 1 - Math.pow(1 - fullscreenProgress, 1.6);
          const revealPanelBleed = reducedMotion
            ? 4
            : lerp(0, 4, Math.max(imageT, fullscreenEased));
          const panelLeft = lerp(
            reclaimedPanelLeft - revealPanelBleed,
            -revealPanelBleed,
            fullscreenEased,
          );
          const panelWidth = lerp(
            reclaimedPanelWidth + revealPanelBleed * 2,
            viewportWidth + revealPanelBleed * 2,
            fullscreenEased,
          );

          return (
            <article
              key={screenToSpace.id}
              className={styles.chapter}
              data-mode={metrics.mode}
              style={
                {
                  transform: `translate3d(${panelLeft}px, 0, 0)`,
                  width: `${panelWidth}px`,
                  opacity: metrics.opacity,
                  zIndex: metrics.zIndex,
                } as CSSProperties
              }
            >
              <div className={styles.transitionPanel} data-chrome-surface="light">
                <div
                  className={styles.transitionInner}
                  style={
                    {
                      opacity: revealTextT,
                      pointerEvents: revealTextT > 0.5 ? "auto" : "none",
                    } as CSSProperties
                  }
                >
                  <span
                    className={styles.transitionEyebrow}
                    style={
                      {
                        opacity: eyebrowT,
                        transform: `translate3d(0, ${(1 - eyebrowT) * 16}px, 0)`,
                      } as CSSProperties
                    }
                  >
                    {screenToSpace.eyebrow}
                  </span>
                  <h2
                    className={styles.transitionTitle}
                    style={
                      {
                        opacity: titleT,
                        transform: `translate3d(${(1 - titleT) * 46}px, 0, 0) scale(${lerp(0.94, 1, titleT)})`,
                        filter: `blur(${(1 - titleT) * 7}px)`,
                      } as CSSProperties
                    }
                  >
                    {screenToSpace.title}
                  </h2>
                  <p
                    className={styles.transitionBody}
                    style={
                      {
                        opacity: bodyT,
                        transform: `translate3d(0, ${(1 - bodyT) * 20}px, 0)`,
                      } as CSSProperties
                    }
                  >
                    {screenToSpace.body}
                  </p>
                </div>

                <div
                  className={styles.revealFrame}
                  data-chrome-surface="dark"
                  style={
                    {
                      transform: `translate3d(${imageTranslateX}%, 0, 0)`,
                      boxShadow: `0 40px 90px rgba(20, 16, 11, ${imageShadowAlpha.toFixed(3)})`,
                      opacity: imageT > 0.001 ? 1 : 0,
                    } as CSSProperties
                  }
                  aria-hidden={imageT <= 0.001}
                >
                  <div className={styles.revealImageBox}>
                    <img
                      src={imageRevealSrc}
                      alt={content.reveal_image.alt}
                      className={styles.revealImage}
                    />

                    <div
                      className={styles.revealCaption}
                      style={{ opacity: captionT } as CSSProperties}
                    >
                      <span>{content.reveal_caption}</span>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          );
        })()}

        {serviceChapters.map((chapter, index) => {
          const metrics = chapterMetrics[index];
          const isFinalChapter = index === lastChapterIndex;
          const reclaimedWidthBefore = getReclaimedWidthBefore(index);
          // Image-stack swap still waits for the shrink to fully settle (mode ===
          // "collapsed"); the text/CTA crossfade above starts earlier than that, while
          // this chapter is still mid-shrink and still showing its own single image.
          const isFilmstripHost = isFinalChapter && metrics.mode === "collapsed";
          const rawContentOpacity =
            isFinalChapter && stage >= crossfadeStart
              ? finalOldOpacity
              : getContentOpacity(metrics, reducedMotion);
          const contentOpacity = rawContentOpacity;
          const ctaOpacity = isFinalChapter ? finalCtaOpacity : 0;
          const stripOpacityBase = reducedMotion
            ? metrics.mode === "active"
              ? 0
              : 1
            : metrics.mode === "future"
              ? 0
              : clamp(1 - Math.max(contentOpacity, ctaOpacity) * 1.6, 0, 1);
          const isArchivedStrip = metrics.mode === "collapsed" && index < lastChapterIndex;
          const stripOpacity = isArchivedStrip
            ? stripOpacityBase * (1 - stripDismissEased)
            : stripOpacityBase;
          // Mirrors the .chapterFrame text-row sizing per breakpoint (see
          // ServicesSection.module.scss) in pixels so it can be animated down to a
          // true 0 for the image-only buffer — a plain CSS percentage/minmax can't
          // reach 0 here because the SCSS floor (280-290px) would keep winning.
          const normalRowMaxPct = viewportWidth <= 900 ? 32 : viewportWidth <= 1380 ? 22 : 18;
          const normalRowMinPx = viewportWidth <= 1380 ? 280 : 290;
          const normalBottomPx = Math.max((viewportHeight * normalRowMaxPct) / 100, normalRowMinPx);
          const bottomPx = isFinalChapter ? lerp(0, normalBottomPx, rowOpenFraction) : 0;
          const topPx = Math.max(viewportHeight - bottomPx, 0);
          // Same final beat as the framed-photo panel above: as it expands to fill
          // the screen, this left-hand stack clears out of the way — fading and
          // receding slightly — so only the full-screen photo is left on screen.
          const clearEased = reducedMotion
            ? fullscreenProgress
            : 1 - Math.pow(1 - fullscreenProgress, 1.6);
          const archivedStripTravel = isArchivedStrip
            ? lerp(0, collapsedStripWidth * 0.42 + 18, stripDismissEased)
            : 0;
          const chapterWidth = isArchivedStrip
            ? metrics.width
            : Math.min(metrics.width + reclaimedWidthBefore, viewportWidth);
          const chapterOpacity = isFinalChapter
            ? metrics.opacity * (1 - clearEased)
            : isArchivedStrip
              ? metrics.opacity * (1 - stripDismissEased)
              : metrics.opacity;
          const chapterLeft = isFinalChapter
            ? metrics.left - reclaimedWidthBefore - clearEased * 60
            : isArchivedStrip
              ? metrics.left - reclaimedWidthBefore - archivedStripTravel
              : metrics.left - reclaimedWidthBefore;

          return (
            <article
              key={chapter.id}
              className={styles.chapter}
              data-mode={metrics.mode}
              style={
                {
                  "--chapter-surface": chapter.surface,
                  "--chapter-accent": chapter.accent,
                  transform: `translate3d(${chapterLeft}px, 0, 0)`,
                  width: `${chapterWidth}px`,
                  opacity: chapterOpacity,
                  zIndex: metrics.zIndex,
                  pointerEvents:
                    (isFinalChapter && clearEased > 0.5) ||
                    (isArchivedStrip && stripDismissEased > 0.2)
                      ? "none"
                      : undefined,
                } as CSSProperties
              }
            >
              <div
                className={styles.chapterFrame}
                style={
                  isFinalChapter
                    ? ({ gridTemplateRows: `${topPx}px ${bottomPx}px` } as CSSProperties)
                    : undefined
                }
              >
                <div className={styles.chapterMedia} data-chrome-surface="dark">
                  {isFilmstripHost ? (
                    <div
                      className={styles.imageStack}
                      style={
                        {
                          gridTemplateRows: stackSlotProgress
                            .map((slotProgress) => `${slotProgress}fr`)
                            .join(" "),
                        } as CSSProperties
                      }
                    >
                      {stackFrames.map((frame) => (
                        <div key={frame.id} className={styles.imageStackSlot}>
                          <div
                            className={styles.chapterImage}
                            style={
                              {
                                backgroundImage: `url('${frame.image}')`,
                                backgroundPosition: frame.imagePosition,
                              } as CSSProperties
                            }
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div
                      className={styles.chapterImage}
                      style={
                        {
                          backgroundImage: `url('${chapter.image}')`,
                          backgroundPosition: chapter.imagePosition,
                          transform: `scale(${metrics.imageScale}) translate3d(${metrics.imageShift}%, 0, 0)`,
                        } as CSSProperties
                      }
                    />
                  )}
                  <div className={styles.chapterImageShade} aria-hidden="true" />
                </div>

                <div
                  className={styles.chapterSurface}
                  data-chrome-surface="dark"
                >
                  {isFinalChapter && (
                    <div
                      className={styles.servicesCta}
                      style={
                        {
                          opacity: ctaOpacity,
                          transform: `translate3d(0, ${(1 - ctaOpacity) * 18}px, 0)`,
                          pointerEvents: ctaOpacity > 0.5 ? "auto" : "none",
                        } as CSSProperties
                      }
                    >
                      <span className={styles.servicesCtaEyebrow}>{content.cta_eyebrow}</span>
                      <h3 className={styles.servicesCtaTitle}>{content.cta_title}</h3>
                      <a className={styles.servicesCtaLink} href={content.cta_href}>
                        {content.cta_label} &#8599;
                      </a>
                    </div>
                  )}
                  <div
                    className={styles.chapterSurfaceInner}
                    style={
                      {
                        opacity: contentOpacity,
                        transform: `translate3d(${(1 - contentOpacity) * 28}px, 0, 0)`,
                        pointerEvents: contentOpacity > 0.01 ? "auto" : "none",
                      } as CSSProperties
                    }
                  >
                    <div className={styles.chapterMeta}>
                      <span className={styles.chapterEyebrow}>{chapter.eyebrow}</span>
                      <span className={styles.chapterLabel}>{chapter.label}</span>
                    </div>

                    <div className={styles.chapterBodyWrap}>
                      <span className={styles.chapterNote}>{chapter.note}</span>
                      <p className={styles.chapterBody}>{chapter.body}</p>
                    </div>

                    <div className={styles.chapterTitleWrap}>
                      <h2 className={styles.chapterTitle}>
                        {chapter.title}
                      </h2>
                    </div>
                  </div>
                </div>

                <div
                  className={styles.stripMeta}
                  style={
                    {
                      opacity: stripOpacity,
                    } as CSSProperties
                  }
                >
                  <span className={styles.stripIndex}>
                    {(index + 1).toString().padStart(2, "0")}
                  </span>
                  <span className={styles.stripLabel}>
                    {chapter.label}
                  </span>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
